import { useState, useEffect, Fragment, useCallback, useRef } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Paper, Lines, LineContent, ContentWrapper } from './variants';
import { Input } from '../../../../core/components/atoms/Input';
import { LoadingIcon } from 'morpheus-ui';
import { useBoardConfig } from '../../../hooks/useBoardConfig';
import { useEssayCoreStore } from '../../../stores/essayCoreStore';

interface IEssayPaper {
  correction: number;
  fontSize: 'small' | 'default' | 'large' | 'verySmall';
  essayText: string;
  essayTitle?: string;
  blocked: boolean;
  loading: boolean;
  setText: (text: string) => void;
  setTitle?: (title: string) => void;
  setIsValid?: (isValid: boolean) => void;
  mobile?: boolean;
}

export default function EssayPaper({
  correction,
  fontSize,
  setText,
  essayText,
  essayTitle,
  setTitle,
  blocked = true,
  loading,
  setIsValid,
  mobile = false,
}: IEssayPaper) {
  const paperRef = useRef<HTMLTextAreaElement>(null);
  const measureRef = useRef<HTMLTextAreaElement>(null);

  const { board } = useBoardConfig(Number(correction) || 2);
  const { setText: setCoreText, setTitle: setCoreTitle } = useEssayCoreStore();

  const [paperLines, setPaperLines] = useState<number>(0);
  const [paperHeight, setPaperHeight] = useState<number>(900);
  const [mobileWidth, setMobileWidth] = useState<number>(0);

  const sizes = {
    default: 16,
    small: 14,
    large: 18,
    verySmall: 12
  };

  const spaces = {
    default: 2,
    small: 1.5,
    verySmall: 1.2,
    large: 2,
  };

  useEffect(() => {
    const LINE_HEIGHT = 30;
    const finalLines = paperLines <= board.lines ? board.paperHeight + 20 : paperLines * LINE_HEIGHT + 20;
    setPaperHeight(paperLines > 30 ? finalLines + 15 : finalLines);
  }, [paperLines, board]);

  const getActualScrollHeight = useCallback(() => {
    if (measureRef.current) {
      measureRef.current.style.wordSpacing = `${spaces[fontSize]}px`;
      measureRef.current.style.fontSize = `${sizes[fontSize]}px`;
      measureRef.current.style.fontFamily = 'Noto Sans';
      measureRef.current.style.fontWeight = 'normal';
      measureRef.current.style.lineHeight = '30px';
      measureRef.current.style.padding = '4px 12px';
      measureRef.current.style.width = mobile && mobileWidth ? `${mobileWidth}px` : `678px`;
      measureRef.current.style.boxSizing = 'border-box';
      
      measureRef.current.value = essayText || '';
      
      return measureRef.current.scrollHeight;
    }
    return 0;
  }, [essayText, fontSize, sizes, spaces, mobile, mobileWidth]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if(setText) {
      setText(e.target.value);
      setCoreText(e.target.value);
    }
  }, [setText, setCoreText]);

  const sanitizePastedText = (text: string) =>
    text
      .replace(/\r\n?/g, '\n')
      .replace(/[^\S\n]+/g, ' ')
      .replace(/ *\n */g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!setText) {
      return;
    }

    e.preventDefault();

    const textarea = paperRef.current;
    if (!textarea) {
      return;
    }

    const pasted = sanitizePastedText(e.clipboardData.getData('text/plain'));
    const current = essayText || '';
    const start = textarea.selectionStart ?? current.length;
    const end = textarea.selectionEnd ?? current.length;

    const newValue = current.slice(0, start) + pasted + current.slice(end);

    setText(newValue);
    setCoreText(newValue);

    requestAnimationFrame(() => {
      const pos = start + pasted.length;
      textarea.setSelectionRange(pos, pos);
    });
  }, [essayText, setText, setCoreText]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if(setTitle) {
      setTitle(e.target.value);
      setCoreTitle(e.target.value);
    }
  }, [setTitle, setCoreTitle]);

  // On mobile the textarea width is fluid, so measure with its real width to keep
  // the ruled lines aligned with the wrapped text.
  useEffect(() => {
    if (!mobile) return;

    const updateWidth = () => {
      const width = paperRef.current?.clientWidth || Math.min(window.innerWidth, 750);
      setMobileWidth(prev => (prev === width ? prev : width));
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [mobile, paperLines]);

  useEffect(() => {
    const actualHeight = getActualScrollHeight();
    const lineHeight = measureRef.current
      ? parseFloat(getComputedStyle(measureRef.current).lineHeight)
      : board.lines;

    const lines = Math.floor(actualHeight / lineHeight);

    if (setIsValid) {
      // Mobile has no line limit
      setIsValid(mobile || lines <= board.lines);
    }

    setPaperLines(lines < board.lines ? board.lines : lines);
  }, [essayText, fontSize, getActualScrollHeight, mobile]);

  return (
    <Fragment>
      {board.title && (
        <div className='m-auto mt-8 mb-4 xs:w-full lg:w-[750px]'>
          <Input
            placeholder='Titulo'
            value={essayTitle}
            onChange={handleTitleChange}
            variant='sm'
          />
        </div>
      )}
      <div>
        <div className='m-auto mb-1 h-5 xs:w-full lg:w-[750px]'>
          {loading ? (
            <div className='flex w-full items-center justify-end'>
              <Typography size='mini' variant='italic' className='text-right animate-pulse'>
                Salvando...
              </Typography>
            </div>
          ) : (
            <Typography size='mini' variant='italic' className='text-right'>
              {essayText !== null || essayText !== "" ? 'Redação salva' : ''}
            </Typography>
          )}
        </div>
        {paperLines === 0 ? (
          <div className='flex w-full items-center justify-center'>
            <LoadingIcon className='stroke-current h-32 animate-spin fill-none text-center' />
          </div>
        ) : (
          <div className={Paper()} style={{ height: paperHeight }}>
            <div className='absolute z-0 h-full w-full'>
              {[...Array(paperLines)].map((__, i) => (
                <div className={Lines()} key={i}>
                  <div className={LineContent()}>
                    <div className='h-4 w-4 rounded-pill bg-color-lighter' />
                    <Typography
                      size='default'
                      className={
                        mobile || i + 1 <= board.lines
                          ? 'text-mono-color-medium'
                          : 'text-red-500'
                      }
                    >
                      {i + 1}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>

            <div className={ContentWrapper()}>
              <textarea
                ref={paperRef}
                value={essayText || ''}
                style={{
                  wordSpacing: spaces[fontSize],
                  fontSize: sizes[fontSize],
                  height: paperHeight
                }}
                disabled={blocked}
                onChange={handleTextChange}
                onPaste={handlePaste}
                className='inline-flex w-full resize-none items-center justify-center overflow-hidden border-color-transparent bg-color-transparent px-3 py-1 text-left font-notoSans font-normal leading-[30px] focus:border-color-transparent focus:ring-0'
                placeholder='Escreva aqui a sua redação'
                spellCheck={false}
                autoCorrect='off'
                autoCapitalize='off'
              />
            </div>
          </div>
        )}
        
        {/* Hidden textarea for measuring scroll height */}
        <textarea
          ref={measureRef}
          style={{
            position: 'absolute',
            top: '-9999px',
            left: '-9999px',
            visibility: 'hidden',
            overflow: 'hidden',
            resize: 'none',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            height: 'auto'
          }}
          readOnly
        />
      </div>
    </Fragment>
  );
}
